import API from '../endpoints';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { LeagueTable } from '@repo/types';
import { getAccessToken } from '../utils/getAccessToken';
import { useGlobalContext } from '@slc/tools/GlobalContext';

// 5 minutes frontend cache for league table query
const CACHE_TIME = 5 * 60 * 1000;

export type LeagueTableQueryArgs = {
  teamId?: string;
  competitionId?: string;
  seasonId?: string;
  /**
   * Which group's table to read, for a competition whose stage an editor grouped (SC-1227).
   *
   * Omitted for every competition that stores one table, which is every league. It is part of the
   * query key by virtue of being part of `args`, so two groups of one competition never share a
   * cache entry — and a caller that does not send it produces the same key it always did.
   */
  groupId?: string;
  /** YYYY-MM-DD: the table rebuilt from completed matches up to the end of that UK day. Not with `teamId`. */
  asOf?: string;
};

type QueryOptions = {
  enabled: boolean;
};

export const useLeagueTableQuery = (args: LeagueTableQueryArgs, options?: QueryOptions) => {
  const { apiUrl } = useGlobalContext();
  const accessToken = getAccessToken();

  return useQuery({
    queryKey: ['league-table', args, apiUrl],
    queryFn: () => fetchLeagueTable(apiUrl, args),
    // `args` is part of the key, so changing the season (SC-1052) or the group asks for a key
    // that has never been fetched — without this the hook reports `isLoading`, every consumer
    // falls back to its skeleton, and the season dropdown the visitor just clicked is unmounted
    // from under them. Keeping the previous table up while the next one loads is benign for the
    // other consumers too: they all render a table that is simply one request out of date.
    placeholderData: keepPreviousData,
    staleTime: !accessToken ? CACHE_TIME : undefined,
    enabled: !!apiUrl && (options?.enabled ?? true),
  });
};

/**
 * The table, or null when the API has none to give: a `teamId` whose club is in no competition answers 200 with an
 * empty body (Nest sends a null result that way, and axios reads it as ''). Null, not undefined — React Query refuses
 * an undefined result — and a settled success, so a club page reads it as "no league" rather than an error.
 */
export const toLeagueTable = (data: LeagueTable | ''): LeagueTable | null => data || null;

const fetchLeagueTable = async (apiUrl: string, params: LeagueTableQueryArgs): Promise<LeagueTable | null> => {
  const { data } = await axios.get<LeagueTable | ''>(`${apiUrl}${API.endpoints.leagueTable}`, {
    params,
  });
  return toLeagueTable(data);
};
